/**
 * API-based Avatar Upload Service
 * Replaces direct Firebase Storage uploads with secure API-based signed URL approach
 */

/* eslint-disable no-console */

import { authenticatedApi } from '../lib/authenticated-api';
import type { ApiResponse } from '../lib/authenticated-api';
import { getUser } from '../lib/services/userService';
import type { PhotoUrls } from '../lib/types/user';

/**
 * Interface for avatar upload service
 */
export interface AvatarUploadService {
  uploadAvatar(file: File, userId: string): Promise<PhotoUrls>;
  cancelUpload(): void;
  onProgress(callback: (progress: number) => void): void;
}

/**
 * Response from photo upload URL endpoint
 */
interface PhotoUploadUrlResponse {
  uploadUrl: string;
  expiresAt: string;
}

/**
 * File names the avatar trigger writes: `<size>_<generation>.jpg`, with the
 * original's generation as an identity, not a timestamp. Legacy timestamp
 * keys also match these digit-only patterns; stored URLs encode the path.
 */
const PROCESSED_AVATAR_FILE = {
  128: /(?:%2F|\/)128_\d+\.jpg/,
  512: /(?:%2F|\/)512_\d+\.jpg/,
};

/**
 * The most pixels an avatar may have, 5000x5000. The avatar trigger refuses
 * an image over it unprocessed (`MAX_INPUT_PIXELS`,
 * functions/src/triggers/avatarProcessing.ts, #2482), so it is refused here
 * before the upload, where the user can be told why.
 */
export const MAX_AVATAR_PIXELS = 25_000_000;

/**
 * The image's width and height as the browser reads them, or undefined when it
 * cannot read the file as an image. An `Image` from an object URL: the load
 * event gives the dimensions without the page drawing the image.
 */
function readImageDimensions(
  file: File
): Promise<{ width: number; height: number } | undefined> {
  return new Promise(resolve => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    const settle = (dimensions?: { width: number; height: number }) => {
      URL.revokeObjectURL(url);
      resolve(dimensions);
    };
    image.onload = () =>
      settle({ width: image.naturalWidth, height: image.naturalHeight });
    image.onerror = () => settle();
    image.src = url;
  });
}

/**
 * Thrown when the processed avatar does not appear in the profile in time.
 * The file was uploaded, so retrying the whole upload would not help.
 */
export class AvatarProcessingTimeoutError extends Error {
  constructor() {
    super('Avatar processing timed out. Please try again.');
    this.name = 'AvatarProcessingTimeoutError';
  }
}

/**
 * API-based implementation of avatar upload service
 * Uses signed URLs from the backend API for secure uploads
 */
export class ApiAvatarUploadService implements AvatarUploadService {
  private progressCallback?: (progress: number) => void;
  private uploadAbortController?: AbortController;

  /**
   * Upload an avatar using the API-based flow
   * @param file - The image file to upload
   * @param userId - The user ID for the avatar
   * @returns Promise that resolves with the processed photo URLs
   */
  async uploadAvatar(file: File, userId: string): Promise<PhotoUrls> {
    console.log('🔄 [AvatarUpload] Starting avatar upload process', {
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type,
      userId,
    });

    // Step 1: Validate file
    console.log('✅ [AvatarUpload] Step 1: Validating file');
    await this.validateFile(file);
    console.log('✅ [AvatarUpload] File validation passed');

    // The stored URLs before this upload, so polling can tell the new
    // avatar from the previous one.
    const previousPhotoUrls = (await getUser(userId))?.photo_url ?? null;

    // Step 2: Request signed URL from API
    console.log('📡 [AvatarUpload] Step 2: Requesting signed URL from API');
    const signedUrlResult = await this.requestSignedUrl(userId);

    console.log('📡 [AvatarUpload] API response received', {
      success: signedUrlResult.success,
      hasData: !!signedUrlResult.data,
      error: signedUrlResult.error,
    });

    if (!signedUrlResult.success || !signedUrlResult.data) {
      console.error('❌ [AvatarUpload] Failed to get signed URL', {
        success: signedUrlResult.success,
        error: signedUrlResult.error,
      });
      throw new Error(signedUrlResult.error || 'Failed to get upload URL');
    }

    const { uploadUrl, expiresAt } = signedUrlResult.data;
    console.log('✅ [AvatarUpload] Signed URL received', {
      uploadUrl: `${uploadUrl.substring(0, 50)}...`,
      expiresAt,
    });

    // Check if URL is about to expire (less than 1 minute left)
    const expiryTime = new Date(expiresAt).getTime();
    const now = Date.now();
    if (expiryTime - now < 60000) {
      console.error('❌ [AvatarUpload] Upload URL is about to expire', {
        expiryTime,
        now,
        timeLeft: expiryTime - now,
      });
      throw new Error('Upload URL is about to expire. Please try again.');
    }

    // Step 3: Upload file to signed URL
    console.log('🔄 [AvatarUpload] Step 3: Uploading file to signed URL');
    await this.uploadToSignedUrl(file, uploadUrl);
    console.log('✅ [AvatarUpload] File uploaded to signed URL successfully');

    // Step 4: Poll for processing completion
    console.log('🔄 [AvatarUpload] Step 4: Polling for processing completion');
    const result = await this.pollForCompletion(userId, previousPhotoUrls);
    console.log(
      '✅ [AvatarUpload] Avatar upload process completed successfully',
      { result }
    );

    return result;
  }

  /**
   * Cancel any ongoing upload
   */
  cancelUpload(): void {
    if (this.uploadAbortController) {
      this.uploadAbortController.abort();
      this.uploadAbortController = undefined;
    }
  }

  /**
   * Register a progress callback
   * @param callback - Function to call with progress updates (0-100)
   */
  onProgress(callback: (progress: number) => void): void {
    this.progressCallback = callback;
  }

  /**
   * Validate the file before upload
   * @param file - The file to validate
   * @throws Error if file is invalid
   */
  private async validateFile(file: File): Promise<void> {
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      throw new Error('Please select a valid image file (JPEG, PNG, or WebP)');
    }

    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      throw new Error('Image size must be less than 5MB');
    }

    // A file the browser cannot read as an image is left to the trigger,
    // which decides from the bytes themselves.
    const dimensions = await readImageDimensions(file);
    if (
      dimensions &&
      dimensions.width * dimensions.height > MAX_AVATAR_PIXELS
    ) {
      throw new Error(
        `Image must be at most 25 megapixels (for example 5000×5000); this one is ${dimensions.width}×${dimensions.height}`
      );
    }
  }

  /**
   * Request a signed URL from the API
   * @param userId - The user ID
   * @returns Promise with the signed URL response
   */
  private async requestSignedUrl(
    userId: string
  ): Promise<ApiResponse<PhotoUploadUrlResponse>> {
    const endpoint = `/v1/users/${userId}/photo:uploadUrl`;
    console.log('📡 [AvatarUpload] Making API request', {
      endpoint,
      method: 'POST',
      userId,
    });

    try {
      const response = await authenticatedApi.request<PhotoUploadUrlResponse>(
        endpoint,
        { method: 'POST' }
      );

      console.log('📡 [AvatarUpload] API request completed', {
        success: response.success,
        hasData: !!response.data,
        error: response.error,
      });

      return response;
    } catch (error) {
      console.error('❌ [AvatarUpload] API request failed with exception', {
        error: error instanceof Error ? error.message : error,
        endpoint,
        userId,
      });

      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'Unknown error during API request',
      };
    }
  }

  /**
   * Upload the file to the signed URL
   * @param file - The file to upload
   * @param uploadUrl - The signed URL to upload to
   */
  private async uploadToSignedUrl(
    file: File,
    uploadUrl: string
  ): Promise<void> {
    console.log('🔄 [AvatarUpload] Starting upload to signed URL', {
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type,
      urlLength: uploadUrl.length,
    });

    // Create abort controller for cancellation
    this.uploadAbortController = new AbortController();

    try {
      // Create XMLHttpRequest for progress tracking
      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        // Track upload progress
        xhr.upload.addEventListener('progress', event => {
          if (event.lengthComputable) {
            const progress = (event.loaded / event.total) * 100;
            console.log('📊 [AvatarUpload] Upload progress', {
              progress: Math.round(progress),
              loaded: event.loaded,
              total: event.total,
            });
            this.progressCallback?.(progress);
          }
        });

        // Handle completion
        xhr.addEventListener('load', () => {
          console.log('✅ [AvatarUpload] Upload request completed', {
            status: xhr.status,
            statusText: xhr.statusText,
          });

          if (xhr.status >= 200 && xhr.status < 300) {
            resolve();
          } else {
            const error = `Upload failed with status ${xhr.status}`;
            console.error('❌ [AvatarUpload] Upload failed', {
              status: xhr.status,
              statusText: xhr.statusText,
              responseText: xhr.responseText,
            });
            reject(new Error(error));
          }
        });

        // Handle errors
        xhr.addEventListener('error', () => {
          console.error('❌ [AvatarUpload] Upload network error');
          reject(
            new Error(
              'Upload failed. Please check your connection and try again.'
            )
          );
        });

        // Handle abort
        xhr.addEventListener('abort', () => {
          console.log('⏹️ [AvatarUpload] Upload cancelled');
          reject(new Error('Upload cancelled'));
        });

        // Set up abort controller
        this.uploadAbortController?.signal.addEventListener('abort', () => {
          console.log('⏹️ [AvatarUpload] Aborting upload via controller');
          xhr.abort();
        });

        // Send the request
        console.log('📡 [AvatarUpload] Sending PUT request to signed URL');
        xhr.open('PUT', uploadUrl);
        xhr.setRequestHeader('Content-Type', file.type);
        xhr.send(file);
      });
    } finally {
      this.uploadAbortController = undefined;
    }
  }

  /**
   * Poll for avatar processing completion
   * @param userId - The user ID
   * @param previousPhotoUrls - The stored URLs before this upload
   * @returns Promise that resolves with the processed photo URLs
   */
  private async pollForCompletion(
    userId: string,
    previousPhotoUrls: PhotoUrls | null,
    maxAttempts = 60
  ): Promise<PhotoUrls> {
    console.log('🔄 [AvatarUpload] Starting polling for completion', {
      userId,
      maxAttempts,
    });

    let attempts = 0;

    while (attempts < maxAttempts) {
      attempts++;
      console.log(
        `🔄 [AvatarUpload] Polling attempt ${attempts}/${maxAttempts}`
      );

      try {
        const user = await getUser(userId);
        const photoUrls = user?.photo_url;

        console.log('📊 [AvatarUpload] Current user photo URLs', {
          photoUrls,
          has128: !!photoUrls?.['128'],
          has512: !!photoUrls?.['512'],
        });

        // Check if processing is complete (both sizes available)
        if (photoUrls?.['128'] && photoUrls['512']) {
          // Ensure URLs are the trigger's processed images, and not the
          // ones stored before this upload
          if (
            PROCESSED_AVATAR_FILE[128].test(photoUrls['128']) &&
            PROCESSED_AVATAR_FILE[512].test(photoUrls['512']) &&
            (photoUrls['128'] !== previousPhotoUrls?.['128'] ||
              photoUrls['512'] !== previousPhotoUrls?.['512'])
          ) {
            console.log(
              '✅ [AvatarUpload] Processing complete - processed images found',
              {
                photoUrls,
              }
            );
            return photoUrls; // Processing complete
          } else {
            console.log(
              '⏳ [AvatarUpload] Photo URLs exist but are not the new processed images',
              {
                url128: photoUrls['128'],
                url512: photoUrls['512'],
              }
            );
          }
        }
      } catch (error) {
        console.warn('⚠️ [AvatarUpload] Error during polling attempt', {
          attempt: attempts,
          error: error instanceof Error ? error.message : error,
        });
        // User might not be found yet, continue polling
      }

      // Wait 1 second before next attempt
      console.log(`⏳ [AvatarUpload] Waiting 1 second before next attempt`);
      await new Promise(resolve => setTimeout(resolve, 1000));
    }

    console.error('❌ [AvatarUpload] Avatar processing timed out', {
      attempts,
      maxAttempts,
    });
    throw new AvatarProcessingTimeoutError();
  }
}

/**
 * Retry configuration for transient failures
 */
interface RetryConfig {
  maxRetries: number;
  baseDelay: number;
  maxDelay: number;
}

/**
 * Enhanced avatar upload service with retry logic
 */
export class RetryableAvatarUploadService implements AvatarUploadService {
  private baseService: ApiAvatarUploadService;
  private retryConfig: RetryConfig;

  constructor(
    retryConfig: RetryConfig = {
      maxRetries: 3,
      baseDelay: 1000,
      maxDelay: 10000,
    }
  ) {
    this.baseService = new ApiAvatarUploadService();
    this.retryConfig = retryConfig;
  }

  async uploadAvatar(file: File, userId: string): Promise<PhotoUrls> {
    let lastError: Error | undefined;

    for (let attempt = 0; attempt <= this.retryConfig.maxRetries; attempt++) {
      try {
        return await this.baseService.uploadAvatar(file, userId);
      } catch (error) {
        lastError = error as Error;

        // Don't retry validation errors, user cancellations, or a poll
        // timeout: the file was already uploaded, and a retry would repeat
        // the signed URL, the upload and the processing
        if (
          lastError instanceof AvatarProcessingTimeoutError ||
          lastError.message.includes('valid image file') ||
          lastError.message.includes('less than 5MB') ||
          lastError.message.includes('megapixels') ||
          lastError.message.includes('cancelled')
        ) {
          throw lastError;
        }

        // Don't retry on last attempt
        if (attempt === this.retryConfig.maxRetries) {
          break;
        }

        // Calculate delay with exponential backoff
        const delay = Math.min(
          this.retryConfig.baseDelay * Math.pow(2, attempt),
          this.retryConfig.maxDelay
        );

        console.warn(
          `Avatar upload attempt ${attempt + 1} failed, retrying in ${delay}ms...`,
          lastError
        );
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }

    throw (
      lastError || new Error('Avatar upload failed after multiple attempts')
    );
  }

  cancelUpload(): void {
    this.baseService.cancelUpload();
  }

  onProgress(callback: (progress: number) => void): void {
    this.baseService.onProgress(callback);
  }
}

// Export singleton instance with retry logic
export const avatarUploadService = new RetryableAvatarUploadService();
